import { getCookie } from '../../utils/cookie-helpers';
import { formatMoney } from '../../utils/currency';
import getSizedImageURL from '../../core/INF-getSizedImageURL';

let theme = window.theme || {};

/**
 * Records which variants must render the blank white box instead of an image.
 *
 * Shopify substitutes the product featured image for a variant that has no image
 * of its own, both in `line_item.image` and in the cart JSON, so an image is
 * always present and cannot be used to detect a colorway that lost its image.
 * A variant needs the blank box only when its product uses variant-level images
 * and this variant is missing one -- otherwise the product image is the correct
 * image to show, which is the common single-image-product case.
 *
 * `window.cartBlankImageVariantIds` is seeded by `layout/theme.liquid` for items
 * already in the cart; this adds newly added items from the product JSON.
 */
const registerBlankImageVariants = (productJson) => {
  if (!productJson?.variants?.length) return;

  const productUsesVariantImages = productJson.variants.some((variant) => variant.featured_image);
  if (!productUsesVariantImages) return;

  const blankImageVariantIds = window.cartBlankImageVariantIds || [];

  productJson.variants.forEach((variant) => {
    if (!variant.featured_image && !blankImageVariantIds.some((id) => String(id) === String(variant.id))) {
      blankImageVariantIds.push(String(variant.id));
    }
  });

  window.cartBlankImageVariantIds = blankImageVariantIds;
};

const generateImageTag = (item) => {
  // `has_blank_image` is set upstream for line items whose product uses
  // variant-level images but whose selected variant has none. Shopify's cart JSON
  // substitutes the product featured image for those variants, so checking
  // `item.image` alone cannot tell us whether the selected colorway has an image.
  if (!item.image || item.has_blank_image === true) {
    item.formatted_image = '<div class="CartProduct__no-image"></div>';
    return item;
  }

  // We do it like this because having an img tag in the template itself will throw a 404 error since it would have an invalid src
  // Optimizely test
  if (window.innerWidth > 767) {
    item.formatted_image = `<img src="${getSizedImageURL(item.image, '128x170_crop_center')}" alt="${
      item.product_title
    }">`;
  } else {
    item.formatted_image = `<img src="${getSizedImageURL(item.image, theme.cart.image_size)}" alt="${
      item.product_title
    }">`;
  }

  return item;
};

const currency_symbol = window.theme.currency_symbol ? window.theme.currency_symbol : '$';
const currency_iso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';
const currency_alternate_iso =
  currency_symbol === '$' && currency_iso !== 'USD' && currency_iso !== 'HKD' ? currency_iso : '';

// Optimizely Test
const formatPrice = (item) => {
  item.formatted_price = ` <span class="CartProduct__price">${currency_alternate_iso}${formatMoney(
    item.price,
    theme.moneyFormat,
  )}</span>`;
  item.discounted = '';
  item.final_sale = '';

  // let compare_at_price = null;
  let cart_items = getCookie('cart_items');
  const final_sale_message = 'Final Sale';

  if (cart_items) {
    cart_items = JSON.parse(cart_items);

    if (cart_items[item.id]) {
      // compare_at_price = cart_items[item.id].compare_at_price;
    }
  }

  if (window.theme.show_currency) {
    item.formatted_price += ` <span class="CartProduct__currency">${window.theme.currency}</span>`;
  }

  if (item.price % 100 === 97 || item.price % 100 === 96) {
    item.final_sale = `<div class="CartProduct__final-sale">${final_sale_message}</div>`;
  }

  return item;
};

const processSimpleVariables = (item, html) => {
  // Loop through all the standard item keys and replace our [[ variables ]] with real values
  // Yes, I basically made a mini templating engine...whatever.
  let updated_html = html;

  Object.entries(item).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([subkey, subvalue]) => {
        let newValue = subvalue;
        if (typeof subvalue === 'string' && subkey === '0') {
          newValue = subvalue.charAt(0) + subvalue.substring(1).toLowerCase();
        }
        if (typeof subvalue === 'object' && subvalue && subvalue.name) {
          newValue = subvalue.name;
        }

        updated_html = updated_html.split(`[[ ${key}.${subkey} ]]`).join(newValue);
      });
    } else {
      updated_html = updated_html.split(`[[ ${key} ]]`).join(value);
    }
  });

  return updated_html;
};

const processOptions = (item, html) => {
  if (html.indexOf('##OPTIONS##') === -1) {
    return html;
  }

  const updated_html = html.split('##OPTIONS##');

  if (!item.variant_title) {
    updated_html.splice(1, 1);
  }

  return updated_html.join('');
};

const processSKU = (item, html) => {
  if (html.indexOf('##SKU##') === -1) {
    return html;
  }

  const updated_html = html.split('##SKU##');

  if (!item.sku) {
    updated_html.splice(1, 1);
  }

  return updated_html.join('');
};

const processVendor = (item, html) => {
  if (html.indexOf('##VENDOR##') === -1) {
    return html;
  }

  const updated_html = html.split('##VENDOR##');

  if (!item.vendor) {
    updated_html.splice(1, 1);
  }

  return updated_html.join('');
};

const processDiscountDescription = (item, html) => {
  if (html.indexOf('##DISCOUNT_DESCRIPTION##') === -1) {
    return html;
  }

  const updated_html = html.split('##DISCOUNT_DESCRIPTION##');

  if (item.discounts.length === 0) {
    // Optimizely Test
    updated_html.splice(1, 1);
  } else {
    // Use the last discount in the array rather than the first...this tends to be the one that is most important.

    // Optimizely Test
    updated_html[1] = updated_html[1]
      .split('[[ discount_description ]]')
      .join(item.discounts[item.discounts.length - 1].title);
    updated_html[0] = updated_html[0].split('[[ has_discount ]]').join('has-discount');
  }

  return updated_html.join('');
};

const processQuantities = (item, html) => {
  if (html.indexOf('##QUANTITIES##') === -1) {
    return html;
  }

  const updated_html = html.split('##QUANTITIES##');
  let quantities;
  let max_quantity = 10;

  if (max_quantity < item.quantity) {
    max_quantity = item.quantity;
  }

  for (let quantity_index = 1; quantity_index <= max_quantity; quantity_index++) {
    let selected = '';

    // console.log(item)

    if (quantity_index === item.quantity) {
      selected = 'selected';
    }

    quantities += updated_html[1]
      .split('[[ quantity_index ]]')
      .join(quantity_index)
      .split('[[ selected ]]')
      .join(selected);
  }

  updated_html[1] = quantities;
  return updated_html.join('');
};

const processPromoMessage = (item, html) => {
  if (window?.product_specific_messaging_cart) {
    const lineItemId = item.id;
    const cartItem = window.cartProductTags.find((cartItem) => cartItem.lineitem_id == lineItemId.toString());
    const message = window.product_specific_messaging_cart;
    if (cartItem) {
      const tag = window.product_specific_messaging_tag;
      if (cartItem.tags.includes(tag)) {
        html = html.replace(
          /<div class="CartItem__promo_message" data-promo-messaging>.*?<\/div>/,
          `<div class="CartItem__promo_message" data-promo-messaging>${message}</div>`,
        );
      } else {
        html = html.replace(/<div class="CartItem__promo_message" data-promo-messaging>.*?<\/div>/, '');
      }
    }
  }
  return html;
};

const processQuantitySelector = (item, html) => {
  if (html.indexOf('##QUANTITY_SELECTOR##') === -1) {
    return html;
  }

  const updated_html = html.split('##QUANTITY_SELECTOR##');

  if (item.product_type.indexOf('GIVEX') !== -1) {
    updated_html[1] = `
    <input style="display: none;"
      class="Quantity__input Quantity__input--readonly"
      type="number"
      name="quantity"
      value="1"
      min="0"
      data-quantity-input
      readonly
    >
    <div class="CartProduct__remove--giftcard" data-cart-remove data-product-id="${item.id}"><i class="far fa-times"></i></div>
    `;
  }

  return updated_html.join('');
};

export {
  registerBlankImageVariants,
  generateImageTag,
  formatPrice,
  processSimpleVariables,
  processOptions,
  processSKU,
  processVendor,
  processDiscountDescription,
  processQuantities,
  processQuantitySelector,
  processPromoMessage,
};
